// This file contains the Select Pick List Card Component
// which is used to render a list of checkboxes for selecting options from a pick list
// This function is called from UserFilterCard, ChartFilterGroupCard and FilterElement.

import { Checkbox, Radio, Skeleton, Stack, TextField } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';

interface ISelectPickListCardProps {
  handleCBChange: (e: any) => void;
  allOptions: string[];
  userSelection: string[];
  shouldExclude: boolean;
  loading?: boolean;
  from?: string;
  isSingleSelection?: boolean;
  disabled?: boolean;

  color?: string;
}

///Render Pick list card from raw select members
export const SelecPickListCard = ({
  handleCBChange,
  allOptions = [],
  userSelection = [],
  shouldExclude,
  loading = false,
  from = '',
  isSingleSelection = false,
  disabled = false,
  color,
}: ISelectPickListCardProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const getOption = (value: string | number | null) => {
    if (value === null) return '(Blank)';
    if (value === '') return '(Empty)';
    return String(value);
  };
  const normalizedAllOptions = allOptions?.map((item: any) =>
    item === null ? '(Blank)' : item === '' ? '(Empty)' : item.toString(),
  );

  const normalizedUserSelection = userSelection?.map((item: any) =>
    item === null ? '(Blank)' : item === '' ? '(Empty)' : item.toString(),
  );

  let filteredOptions = useMemo(() => normalizedAllOptions, [normalizedAllOptions]);

  if (searchTerm) {
    const searchText = searchTerm.toLowerCase().trim();
    filteredOptions = filteredOptions.filter((option) =>
      option.toLowerCase().includes(searchText),
    );
  }

  if (showOnlySelected) {
    filteredOptions = filteredOptions.filter((option) => normalizedUserSelection.includes(option));
  }

  const _selectionMembers = filteredOptions?.map((item: any, index: number) => {
    return (
      <label
        className="UserFilterCheckboxes"
        key={index}
        style={{
          marginLeft: from === 'datasetFilter' ? '0.65rem' : '1px',
          gap: from === 'datasetFilter' ? '0.3rem' : '0.1rem',
        }}
      >
        {isSingleSelection ? (
          <Radio
            sx={{
              transform: 'scale(0.8)',
              '&:hover .MuiSvgIcon-root': {
                color: '#808080',
              },
              '&.Mui-checked .MuiSvgIcon-root': {
                color: '#696969',
              },
            }}
            checked={normalizedUserSelection?.[0] === item}
            name={getOption(item)}
            onChange={handleCBChange}
            disabled={disabled}
          />
        ) : (
          <Checkbox
            checked={normalizedUserSelection?.includes(item)}
            name={getOption(item)}
            style={{ transform: 'scale(0.8)', paddingRight: '0px' }}
            sx={{
              color: color,
              '&.Mui-checked': {
                color: !shouldExclude ? '#a6a6a6' : 'orange',
              },
            }}
            onChange={handleCBChange}
            disabled={disabled}
          />
        )}

        <span
          title={item}
          style={{
            marginLeft: 0,
            justifySelf: 'center',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: color,
            overflow: 'hidden',
            ...(shouldExclude && normalizedUserSelection?.includes(item)
              ? { textDecoration: 'line-through' }
              : {}),
          }}
        >
          {getOption(item)}
        </span>
      </label>
    );
  });

  useEffect(() => {
    if (showOnlySelected && filteredOptions.length < 1) {
      filteredOptions = normalizedAllOptions;
      setShowOnlySelected(false);
    }
  }, [showOnlySelected, normalizedAllOptions]);

  if (loading)
    return (
      <Stack
        spacing={1}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
        <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
        <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
      </Stack>
    );

  return (
    <div className="SelectionMembersCheckBoxArea">
      {normalizedAllOptions.length > 10 && (
        <TextField
          className="custom-search-field"
          variant="outlined"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: color,
              '& .MuiInputBase-input::placeholder': {
                color: color,
                opacity: 1,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: color,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: color,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: color,
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {normalizedUserSelection?.length > 0 && (
                  <Tooltip
                    title={
                      showOnlySelected
                        ? 'Click to show all the items'
                        : 'Click to show only the selected items'
                    }
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px',
                        },
                      },
                    }}
                  >
                    <button
                      onClick={() => {
                        setShowOnlySelected((prev) => {
                          const next = !prev;
                          if (next) setSearchTerm('');
                          return next;
                        });
                      }}
                      style={{
                        backgroundColor: showOnlySelected ? 'rgb(173,173,173,0.9)' : '#ffffff',
                        color: showOnlySelected ? '#ffffff' : 'grey',
                        border: showOnlySelected ? 'none' : '1px solid grey',
                        borderRadius: '50%',
                        width: '14px',
                        height: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.35rem',
                        cursor: 'pointer',
                        padding: 0,
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {normalizedUserSelection?.length || 0}
                    </button>
                  </Tooltip>
                )}
                <img
                  src="/glass.png"
                  alt="Search Icon"
                  style={{
                    width: '16px',
                    height: '18px',
                    marginRight: '-9px',
                    paddingBottom: '2px',
                    cursor: 'default',
                    filter: color === '#ffffff' ? 'invert(1)' : 'invert(0)',
                  }}
                />
              </div>
            ),
          }}
        />
      )}
      {_selectionMembers}
    </div>
  );
};
